import getConfig from 'next/config';
import defaultsDeep from 'lodash/defaultsDeep';

const { publicRuntimeConfig } = getConfig();

const apiUrl =
  publicRuntimeConfig.environment === 'production'
    ? 'https://rs-client-api.deliveroo.net'
    : 'https://rs-client-api-staging.deliveroo.net';

const defaultOptions = {
  options: {
    headers: {
      'X-Roo-Restaurant-Manager-Client': 'web-rom',
    },
  },
};

const addApiTrackingHeader = (body) => defaultsDeep(body, defaultOptions);

export const setSelectedOrder = (selectedOrder) => ({
  type: 'SET_SELECTED_ORDER',
  selectedOrder,
});

export const clearSelectedOrder = () => ({
  type: 'CLEAR_SELECTED_ORDER',
});

export const getActiveOrders = (
  restaurantId,
  rejectedOrderId,
  acceptedOrderId,
  isPolling,
) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/restaurants/${restaurantId}/active_orders`,
    type: 'GET_ACTIVE_ORDERS',
    extra: {
      rejectedOrderId,
      acceptedOrderId,
      isPolling,
      branchId: restaurantId,
    },
  });

export const getCompletedOrders = (restaurantId) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/restaurants/${restaurantId}/completed_orders`,
    type: 'GET_COMPLETED_ORDERS',
    extra: {
      branchId: restaurantId,
    },
  });

export const getRestaurantInfo = (restaurantId) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}`,
    type: 'GET_RESTAURANT_INFO',
    extra: {
      branchId: restaurantId,
    },
  });

export const acceptOrder = (restaurantId, orderId, token, adjustment) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/orders/${orderId}`,
    type: 'ACCEPT_ORDER',
    options: {
      method: 'PUT',
      headers: {
        'x-session-auth': `${restaurantId}:${token}`,
      },
      body: {
        manual_eta_adjustment: adjustment,
        status: 'ACCEPTED',
      },
      extra: {
        branchId: restaurantId,
      },
    },
  });

export const rejectOrder = (restaurantId, orderId, token, rejectionReason) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/orders/${orderId}`,
    type: 'REJECT_ORDER',
    options: {
      method: 'PUT',
      headers: {
        'x-session-auth': `${restaurantId}:${token}`,
      },
      body: {
        manual_eta_adjustment: 0,
        status: 'REJECTED',
        reject_reason: rejectionReason,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const updateAvailability = (
  restaurantId,
  unavailableItems,
  token,
  notOfferedItems,
) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/${restaurantId}/unavailable_items`,
    type: 'SET_UNAVAILABLE_ITEMS',
    options: {
      method: 'PUT',
      headers: {
        'x-session-auth': `${restaurantId}:${token}`,
      },
      body: {
        menu_items: unavailableItems,
        not_offered_items: notOfferedItems,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const updateRestaurantStatus = (restaurantId, status) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}`,
    type: 'UPDATE_RESTAURANT_STATUS',
    options: {
      method: 'PATCH',
      body: {
        status,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const getOneTimePass = (restaurantId) =>
  addApiTrackingHeader({
    endpoint: `/api/live_orders_logon_pass`,
    type: 'GET_ONE_TIME_PASS',
    options: {
      method: 'POST',
      body: {
        restaurant_id: restaurantId,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const logonWithPass = (pass) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/logon-with-pass`,
    type: 'LOGON_WITH_PASS',
    options: {
      method: 'POST',
      body: {
        pass,
      },
    },
  });

export const endRejectInProgress = () => ({
  type: 'END_REJECT_IN_PROGRESS',
});

export const getMenuItems = (restaurantId) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/${restaurantId}/menu`,
    type: 'GET_MENU_ITEMS',
    extra: {
      branchId: restaurantId,
    },
  });

export const setWorkload = (restaurantId, quiet, moderate, busy) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/workload`,
    type: 'SET_WORKLOAD',
    options: {
      method: 'PUT',
      body: {
        busy,
        moderate,
        quiet,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const updateCurrentWorkload = (restaurantId, workload, busyPrepTime) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/workload`,
    type: 'UPDATE_CURRENT_WORKLOAD',
    options: {
      method: 'PUT',
      body: {
        current_load: workload,
        user_specified: busyPrepTime,
      },
    },
    extra: {
      branchId: restaurantId,
    },
  });

export const markAsOutForDelivery = (restaurantId, orderId) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/orders/${orderId}`,
    type: 'MARK_AS_OUT_FOR_DELIVERY',
    options: {
      method: 'PUT',
      body: {
        status: 'IN_TRANSIT',
      },
    },
    extra: {
      branchId: restaurantId,
      hasMarkedAsOutForDelivery: true,
    },
  });

export const markAsPickedUpByCustomer = (restaurantId, orderId) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}/orders/${orderId}`,
    type: 'MARK_AS_PICKED_UP_BY_CUSTOMER',
    options: {
      method: 'PUT',
      body: {
        status: 'PICKED_UP_BY_CUSTOMER',
      },
    },
  });

export const submitPickupInstructions = (restaurantId, instruction) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}`,
    type: 'SUBMIT_PICKUP_INSTRUCTIONS',
    options: {
      method: 'PATCH',
      body: {
        fulfillment_types: [
          {
            enabled: true,
            note: instruction,
            type: 'customer_collection',
          },
        ],
      },
    },
  });

export const setPickupPrepTime = (restaurantId, prepTime) =>
  addApiTrackingHeader({
    endpoint: `${apiUrl}/rom/v2/restaurants/${restaurantId}`,
    type: 'SET_PICKUP_PREP_TIME',
    options: {
      method: 'PATCH',
      body: {
        collection_prep_time: prepTime,
      },
    },
  });

export const createOrder = (restaurantId, details, destination) => ({
  endpoint: `/api/restaurants/${restaurantId}/orders`,
  type: 'CREATE_ORDER',
  options: {
    method: 'POST',
    body: {
      details,
      destination,
    },
  },
});
